import { useEffect, useRef, useState, type FocusEvent, type FormEvent } from 'react';
import { X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const entityTypes = ['study', 'organization', 'user', 'voucher', 'interview'] as const;
type SearchEntityType = (typeof entityTypes)[number];

const labels: Record<SearchEntityType, string> = {
  study: 'Studies',
  organization: 'Organizations',
  user: 'Users',
  voucher: 'Vouchers',
  interview: 'Interviews',
};

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function selectedTypesFromSearch(search: string): SearchEntityType[] {
  const selected = new Set(new URLSearchParams(search).get('entityTypes')?.split(',') ?? []);
  return entityTypes.filter((entityType) => selected.has(entityType));
}

export function GlobalSearchForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(new URLSearchParams(location.search).get('q') ?? '');
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<SearchEntityType[]>(() =>
    selectedTypesFromSearch(location.search)
  );
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isSearchControlFocused, setIsSearchControlFocused] = useState(false);
  const isUuid = uuidPattern.test(value.trim());

  useEffect(() => {
    const query = new URLSearchParams(location.search).get('q');
    if (query === null) return;
    setValue(query);
    setSelectedEntityTypes(selectedTypesFromSearch(location.search));
  }, [location.search]);

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const query = value.trim();
    if (!query) return;
    const params = new URLSearchParams({ q: query });
    if (selectedEntityTypes.length > 0) params.set('entityTypes', selectedEntityTypes.join(','));
    setIsPanelOpen(false);
    navigate(`/search?${params.toString()}`);
  }

  function closePanelWhenFocusLeaves(event: FocusEvent<HTMLDivElement>) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsPanelOpen(false);
      setIsSearchControlFocused(false);
    }
  }

  function toggleEntityType(entityType: SearchEntityType) {
    setSelectedEntityTypes((selected) =>
      selected.includes(entityType)
        ? selected.filter((current) => current !== entityType)
        : entityTypes.filter((current) => selected.includes(current) || current === entityType)
    );
  }

  function clearSearch() {
    setValue('');
    setIsPanelOpen(false);
    inputRef.current?.focus();
  }

  return (
    <div
      className="relative min-w-0"
      onFocus={() => setIsSearchControlFocused(true)}
      onBlur={closePanelWhenFocusLeaves}
    >
      <form className="relative min-w-0" onSubmit={submit}>
        <input
          ref={inputRef}
          aria-label="Search"
          placeholder="Search"
          value={value}
          onFocus={() => {
            setIsSearchControlFocused(true);
            if (value.trim()) setIsPanelOpen(true);
          }}
          onChange={(event) => {
            setValue(event.target.value);
            setIsPanelOpen(Boolean(event.target.value.trim()));
          }}
          className={`focus-ring block min-w-0 w-full border border-neutral-300 px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-500 transition-[border-radius,padding] duration-200 motion-reduce:transition-none ${
            isSearchControlFocused ? 'rounded-md pr-28' : value ? 'rounded-md pr-10' : 'rounded-md'
          }`}
        />
        {value ? (
          <button
            type="button"
            aria-label="Clear search"
            onMouseDown={(event) => event.preventDefault()}
            onClick={clearSearch}
            className={`focus-ring absolute inset-y-0 flex w-8 items-center justify-center text-neutral-500 transition-[right,color] duration-200 hover:text-neutral-900 motion-reduce:transition-none ${
              isSearchControlFocused ? 'right-20' : 'right-0'
            }`}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
        <div
          aria-hidden={!isSearchControlFocused}
          className={`absolute inset-y-0 right-0 w-20 overflow-hidden rounded-r-md ${
            isSearchControlFocused ? '' : 'pointer-events-none'
          }`}
        >
          <button
            type="submit"
            tabIndex={isSearchControlFocused ? undefined : -1}
            className={`focus-ring h-full w-full bg-neutral-900 px-3 text-sm font-medium text-white transition-[transform,opacity] duration-200 ease-out hover:bg-neutral-700 motion-reduce:transition-none ${
              isSearchControlFocused ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
            }`}
          >
            Search
          </button>
        </div>
      </form>
      <div
        aria-hidden={!isPanelOpen}
        className={`absolute left-0 top-full z-50 mt-2 grid w-full transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none ${
          isPanelOpen
            ? 'grid-rows-[1fr] opacity-100'
            : 'pointer-events-none grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <fieldset
            disabled={!isPanelOpen}
            className="rounded-md border border-backoffice-200 bg-backoffice-50 p-2 shadow-lg"
          >
            <legend className="sr-only">Narrow your search</legend>
            <p className="text-xs font-medium text-neutral-700">Narrow your search:</p>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1.5">
              {entityTypes.map((entityType) => {
                const isInterview = entityType === 'interview';
                const isDisabled = isInterview && !isUuid;
                return (
                  <label
                    key={entityType}
                    onMouseDown={(event) => event.preventDefault()}
                    className={`flex items-center gap-1.5 text-xs text-neutral-700 ${
                      isDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEntityTypes.includes(entityType)}
                      disabled={isDisabled}
                      onChange={() => toggleEntityType(entityType)}
                      className="h-3.5 w-3.5 rounded border-neutral-400"
                    />
                    {labels[entityType]}
                  </label>
                );
              })}
            </div>
          </fieldset>
        </div>
      </div>
    </div>
  );
}
